import _ from "underscore";
import ProductImagePath from "helpers/product-image-path";
import ProductImagePathHelpers from "helpers/product-image-path-helpers";
import MembershipHelpers from "helpers/membership-helpers";
import MembershipUpgradesHelpers from "helpers/membership-upgrades-helpers";
import { membershipItemStates, membershipSlotTypes } from "rtr-constants";
import { skuInfo } from "helpers/sku-helpers";
import { usdPriceIntoInt } from "helpers/PricingHelper";
import { ProductImageOrientation, ProductImageSize } from "./product-image-helpers";

const MembershipStateContentHelper = {
  shouldAutoAdvanceCard: function (prevProps, props) {
    const previouslyUnstartedReturnFlow = _.isUndefined(prevProps.returnFlow);
    const nowStartedReturnFlow = !_.isUndefined(props.returnFlow);
    const previouslyClearReturnFlow = _.isEmpty(prevProps.returnFlow);
    const nowInProgressReturnFlow = !_.isEmpty(props.returnFlow);
    const previousPromisedItems = prevProps?.returnFlow?.promised || [];
    const previousKeptItems = prevProps?.returnFlow?.kept || [];
    const currentPromisedItems = props?.returnFlow?.promised || [];
    const currentKeptItems = props?.returnFlow?.kept || [];
    const previousMembershipItemsPurchased = _.filter(
      prevProps?.membershipState?.membershipItems || [],
      item => item.state === membershipItemStates.PURCHASED
    );
    const currentMembershipItemsPurchased = _.filter(
      props?.membershipState?.membershipItems || [],
      item => item.state === membershipItemStates.PURCHASED
    );
    const returnFlowStarted = previouslyUnstartedReturnFlow && nowStartedReturnFlow;
    const returnFlowChanged = previouslyClearReturnFlow && nowInProgressReturnFlow;
    const promisedItemsChanged = currentPromisedItems.length !== previousPromisedItems.length;
    const purchasedItemsChanged = currentMembershipItemsPurchased.length !== previousMembershipItemsPurchased.length;
    const keptItemsChanged = previousKeptItems.length !== currentKeptItems.length;

    return returnFlowStarted || returnFlowChanged || promisedItemsChanged || purchasedItemsChanged || keptItemsChanged;
  },

  shouldAutoScrollToCard: function (oldBagCount, newBagCount, oldMembershipState, newMembershipState) {
    const previousMembershipBagItems = oldBagCount;
    const currentMembershipBagItems = newBagCount;
    const previousMembershipItems = oldMembershipState?.membershipItems || [];
    const currentMembershipItems = newMembershipState?.membershipItems || [];
    const bagItemsChange = previousMembershipBagItems !== currentMembershipBagItems;
    const shipmentItemsChange = previousMembershipItems.length !== currentMembershipItems.length;

    const getSwapCount = items =>
      items.reduce((count, item) => {
        if (item.state === membershipItemStates.RETURN_PROMISE) {
          return count + 1;
        }
        return count;
      }, 0);

    const prevSwappedItemsCount = getSwapCount(previousMembershipItems);
    const currSwappedItemsCount = getSwapCount(currentMembershipItems);

    if (prevSwappedItemsCount !== currSwappedItemsCount) {
      return true;
    }

    return bagItemsChange || shipmentItemsChange;
  },

  shouldAutoScrollToFirstOpenSlotCard: function (membershipState, smBag) {
    if (!membershipState || !smBag || smBag.smBagLoading) {
      return false;
    }

    const numberOfOpenSlots = MembershipHelpers.getOpenSlotsFromMembershipState(membershipState);

    const numberOfItemsInBag = smBag?.items?.arriving?.length ?? 0;

    const slotsNotFilled = numberOfOpenSlots - numberOfItemsInBag;
    return slotsNotFilled > 0;
  },

  fetchImgSrc: function (productData) {
    // On non-prod environments `productData` may be invalid or an empty object so we have to guard against that
    if (!productData || Object.entries(productData).length === 0) {
      return "";
    }

    // This is a temporary fix until membership_bag_actions uses the same ruby classes to both add and fetch membershipBag.
    // Currently adding to membershipBag returns an image path without the ProductImagePath,
    // while the fetch_membership_bag returns a complete image path with ProductImagePath.
    if (productData.imgSrc) {
      const hasProductImagePath = ProductImagePathHelpers.isFullPath(productData.imgSrc);
      return hasProductImagePath ? productData.imgSrc : ProductImagePath + productData.imgSrc;
    }

    // MH Explanation 7-31-19: some accessories don't have a "noModel" image,
    // so we have to default to "front" if its unavailable
    let imageType = ProductImageOrientation.FRONT.path;
    if (productData?.images?.[ProductImageOrientation.NO_MODEL.path]) {
      imageType = ProductImageOrientation.NO_MODEL.path;
    }

    const imageSize = ProductImageSize.x183.path;

    return this.prependProductImagePath(productData, imageType, imageSize);
  },

  // When items comes back from membershipState they just have the images suffix array,
  // which always needs to prepend the ProductImagePath.
  prependProductImagePath: function (productData, imageType, imageSize) {
    if (productData?.images?.[imageType]) {
      return ProductImagePath + productData?.images?.[imageType]?.[imageSize];
    }

    return ProductImagePath + productData?.images?.[ProductImageOrientation.FRONT.path]?.[imageSize];
  },

  maxItemCards: function (membershipState, membershipBag) {
    // ALAN CHEN 12/16/2019 - When the user is not eligibleToPick we shouldn't surface open slot or upgrade messaging
    if (!MembershipHelpers.isEligibleToPick(membershipState)) {
      return this.numItemsAtHome(membershipState) + (membershipBag?.items?.length || 0);
    }

    const isMemberInPick = MembershipHelpers.isShipmentActionPickOrPickOnly(membershipState);
    const upgradeCtaCardCount = Number(!!isMemberInPick); // the Upgrade CTA appears as a static card when the user is in pick - it's always the last item after membership items.

    return (
      this.numItemsAtHome(membershipState) +
      MembershipHelpers.getOpenSlotsFromMembershipState(membershipState) +
      MembershipUpgradesHelpers.getUpgradesInBag(membershipState, membershipBag) +
      upgradeCtaCardCount
    );
  },

  maxItemCardsCE: function (membershipState, itemsInBag) {
    if (!MembershipHelpers.isEligibleToPick(membershipState)) {
      return this.numItemsAtHome(membershipState) + itemsInBag;
    }

    const isMemberInPick = MembershipHelpers.isShipmentActionPickOrPickOnly(membershipState);

    return (
      this.numItemsAtHome(membershipState) +
      MembershipHelpers.getOpenSlotsFromMembershipState(membershipState) +
      MembershipUpgradesHelpers.getUpgradesInCEBag(membershipState, itemsInBag) +
      Number(!!isMemberInPick)
    );
  },

  maxItemCardsSM: function (membershipState, smBag) {
    const itemsInBag = smBag?.items?.arriving ?? [];
    if (!MembershipHelpers.isEligibleToPick(membershipState)) {
      return this.numItemsAtHome(membershipState) + itemsInBag.length;
    }

    const numDisabledBonusSlots =
      membershipState?.disabledSlots?.filter(item => item.type === membershipSlotTypes.BONUS).length || 0;

    const isMemberInPick = MembershipHelpers.isShipmentActionPickOrPickOnly(membershipState);

    return (
      this.numItemsAtHome(membershipState) +
      MembershipHelpers.getOpenSlotsFromMembershipState(membershipState) +
      smBag?.numUpgradesInBag +
      Number(!!isMemberInPick) +
      numDisabledBonusSlots
    );
  },

  numItemsAtHome: function (membershipState) {
    return this.membershipItems(membershipState).length;
  },

  numItemsInBag: function (membershipBag) {
    return membershipBag?.items?.length;
  },

  membershipItems: function (membershipState) {
    return membershipState?.membershipItems || [];
  },

  membershipStateItemPurchaseCharge: function (membershipState) {
    return membershipState?.itemPurchaseCharge || [];
  },

  itemPurchaseCharge: function (membershipState, item) {
    return _.find(this.membershipStateItemPurchaseCharge(membershipState), itemCharge => {
      const purchaseItem = _.first(itemCharge.purchaseItems);
      const styleName =
        typeof purchaseItem.sku === "string" ? skuInfo(purchaseItem.sku).id : purchaseItem.sku.styleName;
      return styleName === item.productId;
    });
  },

  membershipStateProducts: function (membershipState) {
    return membershipState?.products || [];
  },

  /**
   * ProductData is metadata describing an item (i.e. style, category)
   * @param {Object} membershipState
   * @param {String} styleName - Refers to item style
   * @returns {Object} Either an object representing the product from `membershipState` or an empty object
   */
  findProductData: function (membershipState, styleName) {
    const productData = this.membershipStateProducts(membershipState).find(prod => prod.id === styleName);

    return productData ? productData : {};
  },

  defaultProfile: function (paymentProfiles) {
    return _.findWhere(paymentProfiles, { default: true });
  },

  fetchRetailPrice: function (productData) {
    return usdPriceIntoInt(productData?.retailPrice);
  },

  productHasSizes: function (productData) {
    // if length is undefined, will coerce to false
    return productData?.sizes?.length > 1;
  },

  styleNameFromSku: function (sku) {
    if (sku) {
      return _.first(sku.split("_"));
    }
  },

  sizeFromSku(sku) {
    if (sku) {
      return _.last(sku.split("_"));
    }
  },
};

export default MembershipStateContentHelper;

export const {
  shouldAutoAdvanceCard,
  shouldAutoScrollToCard,
  shouldAutoScrollToFirstOpenSlotCard,
  fetchImgSrc,
  prependProductImagePath,
  maxItemCards,
  numItemsAtHome,
  numItemsInBag,
  membershipItems,
  membershipStateItemPurchaseCharge,
  itemPurchaseCharge,
  membershipStateProducts,
  findProductData,
  defaultProfile,
  fetchRetailPrice,
  productHasSizes,
  styleNameFromSku,
  sizeFromSku,
} = MembershipStateContentHelper;
